import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Home, RefreshCw, Loader2, Database, Boxes, GitBranch,
  Search, Clock, Play, Terminal, CheckCircle2, AlertTriangle,
  Trash2, ShieldAlert, Eye, Truck,
} from "lucide-react";
import { SapSyncHistory } from "@/components/SapSyncHistory";
import { SupplierAliasPanel } from "@/components/SupplierAliasPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/** Format ISO → "12 Feb 14:32" style. Returns "—" for falsy. */
function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

/** Small pill showing sync outcome (ok / error / never). */
function StatusPill({ ok, error }) {
  if (error) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-fiori-caption font-semibold rounded-sm bg-red-50 text-red-700 border border-red-200">
        <AlertTriangle size={11} /> error
      </span>
    );
  }
  if (ok) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-fiori-caption font-semibold rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 size={11} /> ok
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-fiori-caption font-semibold rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
      never run
    </span>
  );
}

export default function SapData() {
  const nav = useNavigate();
  // Deep-link support: `/admin/sap-data?tab=cleanup` opens straight to
  // the Data Cleanup tab (SapHub links directly to it).
  const initialTab = (() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["items", "bom", "probe", "cleanup"].includes(t) ? t : "items";
  })();
  const [tab, setTab] = useState(initialTab);
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [triggering, setTriggering] = useState(null);

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const r = await axios.get(`${API}/admin/sap/sync/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401) nav("/login");
      else if (s === 403) { toast.error("Admin only"); nav("/"); }
      else toast.error(e?.response?.data?.detail || "Failed to load SAP status");
    } finally {
      setLoadingStatus(false);
    }
  }, [nav]);

  useEffect(() => {
    refreshStatus();
    // Auto-poll while a sync is running.
    const id = setInterval(() => {
      // Only refetch if we know something is running OR haven't loaded yet.
      setStatus((prev) => {
        if (prev?.running) refreshStatus();
        return prev;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [refreshStatus]);

  const trigger = async (what) => {
    setTriggering(what);
    try {
      await axios.post(`${API}/admin/sap/sync/${what}`, null, { headers: authHeaders() });
      toast.success(`SAP ${what} sync started`);
      setTimeout(refreshStatus, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to trigger ${what} sync`);
    } finally {
      setTriggering(null);
    }
  };

  // Escape hatch when a sync gets stuck (unbounded broker call, dead
  // worker on the other replica, redeploy that killed a BackgroundTask
  // mid-flight). Nukes the DB-backed lease so a fresh sync can grab it.
  const forceClearSync = async () => {
    if (!window.confirm("Force-clear the SAP sync lease? Use this only if a sync has been stuck for more than 10 minutes. Any zombie worker will exit on its next heartbeat.")) return;
    setTriggering("force-clear");
    try {
      const r = await axios.post(
        `${API}/admin/sap/sync/force-clear`, null,
        { headers: authHeaders() });
      const was = r.data?.was_state?.running || "(none)";
      toast.success(`SAP sync lease cleared (was: ${was}). You can trigger a new sync now.`);
      setTimeout(refreshStatus, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force-clear failed");
    } finally {
      setTriggering(null);
    }
  };

  // Seconds since an ISO timestamp — used to decide when to surface
  // the force-clear button. `NaN`/invalid dates fall back to 0.
  const staleSince = (iso) => {
    if (!iso) return 0;
    const t = Date.parse(iso);
    if (isNaN(t)) return 0;
    return Math.floor((Date.now() - t) / 1000);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/sap")}
                    className="text-white hover:bg-white/10"
                    data-testid="sap-back-sap">
              <ArrowLeft size={16} className="mr-1" /> SAP Workspace
            </Button>
            <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                    className="text-white hover:bg-white/10"
                    data-testid="sap-back-home">
              <Home size={16} className="mr-1" /> Home
            </Button>
            <div className="hidden sm:block h-6 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <Database size={18} />
              <div className="text-sm sm:text-base font-bold tracking-tight">SAP Data</div>
              <span className="text-fiori-caption uppercase tracking-widest bg-amber-400/90 text-amber-950 px-1.5 py-0.5 rounded-sm font-bold">
                temporary
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={refreshStatus}
              disabled={loadingStatus}
              className="text-white hover:bg-white/10"
              data-testid="refresh-status-btn"
            >
              {loadingStatus ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="ml-1 hidden sm:inline">Refresh status</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-5">
        {/* Top summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <SyncCard
            title="Item Master"
            icon={<Boxes size={16} />}
            meta={status?.items}
            running={status?.running === "items" || status?.running === "all"}
            onTrigger={() => trigger("items")}
            triggering={triggering === "items"}
            testid="sync-items"
          />
          <SyncCard
            title="BOM Headers"
            icon={<GitBranch size={16} />}
            meta={status?.bom}
            running={status?.running === "bom" || status?.running === "all"}
            onTrigger={() => trigger("bom")}
            triggering={triggering === "bom"}
            testid="sync-bom"
          />
          <SyncCard
            title="BOM Material Inputs"
            icon={<GitBranch size={16} />}
            meta={status?.bom_items}
            running={status?.running === "bom_items" || status?.running === "all"}
            onTrigger={() => trigger("bom_items")}
            triggering={triggering === "bom_items"}
            testid="sync-bom-items"
          />
          <SyncCard
            title="BOM Line Items"
            icon={<GitBranch size={16} />}
            meta={status?.bom_line_items}
            running={status?.running === "bom_line_items" || status?.running === "all"}
            onTrigger={() => trigger("bom_line_items")}
            triggering={triggering === "bom_line_items"}
            testid="sync-bom-line-items"
          />
          <SyncCard
            title="BOM Lines (Mat + Qty)"
            icon={<GitBranch size={16} />}
            meta={status?.bom_lines}
            running={status?.running === "bom_lines" || status?.running === "all"}
            onTrigger={() => trigger("bom_lines")}
            triggering={triggering === "bom_lines"}
            testid="sync-bom-lines"
          />
          <SyncCard
            title="Purchasing Status"
            icon={<Boxes size={16} />}
            meta={status?.material_purchasing}
            running={status?.running === "material_purchasing" || status?.running === "all"}
            onTrigger={() => trigger("material_purchasing")}
            triggering={triggering === "material_purchasing"}
            testid="sync-material-purchasing"
          />
          <SyncCard
            title="Suppliers"
            icon={<Truck size={16} />}
            meta={status?.suppliers}
            running={status?.running === "suppliers" || status?.running === "all"}
            onTrigger={() => trigger("suppliers")}
            triggering={triggering === "suppliers"}
            testid="sync-suppliers"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={() => trigger("all")}
            disabled={triggering === "all" || status?.running !== null}
            data-testid="sync-all-btn"
          >
            {triggering === "all"
              ? <Loader2 size={14} className="animate-spin mr-1" />
              : <Play size={14} className="mr-1" />}
            Sync All Now (Items + BOM + BOM Items)
          </Button>
          {status?.running && (
            <div className="text-xs text-slate-600 inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" />
              Sync <span className="font-semibold">{status.running}</span> in progress
              {status.started_at && <> — started {fmtWhen(status.started_at)}</>}
              {status.lease && !status.lease.is_mine && (
                <span className="ml-1 text-slate-400">· owner {(status.lease.owner || "").slice(0, 10)}…</span>
              )}
            </div>
          )}
          {/* Force-clear escape hatch — visible whenever a sync has been
              "running" for more than 10 minutes. Nukes the DB-backed
              lease so admins can retry without a pod restart. Deployer
              RCA Sep-26 recommended this as the belt-and-suspenders on
              top of the auto-expiring TTL lease. */}
          {status?.running && status?.started_at && staleSince(status.started_at) > 600 && (
            <Button
              variant="outline" size="sm"
              onClick={forceClearSync}
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              disabled={triggering === "force-clear"}
              data-testid="sync-force-clear-btn"
              title="This sync has been running for over 10 minutes and looks stuck. Clears the DB-backed lease so a new sync can start on either replica.">
              {triggering === "force-clear"
                ? <Loader2 size={14} className="animate-spin mr-1" />
                : <ShieldAlert size={14} className="mr-1" />}
              Force-clear stuck sync
            </Button>
          )}
        </div>

        <SapSyncHistory />
        <SupplierAliasPanel />

        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-sm p-1 inline-flex" data-testid="sap-tabs">
          {["items", "bom", "probe", "cleanup"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${
                tab === t ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t === "items" && <><Boxes size={14} /> Items</>}
              {t === "bom" && <><GitBranch size={14} /> BOM</>}
              {t === "probe" && <><Terminal size={14} /> OData Probe</>}
              {t === "cleanup" && <><Trash2 size={14} /> Data Cleanup</>}
            </button>
          ))}
        </div>

        {tab === "items" && <ItemsTab />}
        {tab === "bom" && <BomTab />}
        {tab === "probe" && <ProbeTab />}
        {tab === "cleanup" && <CleanupTab />}
      </main>
    </div>
  );
}

function SyncCard({ title, icon, meta, running, onTrigger, triggering, testid }) {
  const s = meta || {};
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col gap-3"
         data-testid={`${testid}-card`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {icon} {title}
        </div>
        <StatusPill ok={s.ok === true} error={s.error} />
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-fiori-caption">Rows cached</div>
          <div className="text-lg font-extrabold num text-slate-900">{s.rows_in_cache ?? "—"}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-fiori-caption">Last sync</div>
          <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1">
            <Clock size={11} /> {fmtWhen(s.last_synced_at)}
          </div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-fiori-caption">Duration</div>
          <div className="text-sm font-semibold text-slate-800 num">
            {s.elapsed_ms != null ? `${Math.round(s.elapsed_ms / 100) / 10}s` : "—"}
          </div>
        </div>
      </div>
      {s.error && (
        <div className="text-xs bg-red-50 border border-red-200 text-red-800 px-2 py-1.5 rounded-sm mono break-all">
          {s.error}
        </div>
      )}
      <div>
        <Button size="sm" onClick={onTrigger} disabled={triggering || running}
                data-testid={`${testid}-trigger-btn`}>
          {triggering || running
            ? <Loader2 size={14} className="animate-spin mr-1" />
            : <RefreshCw size={14} className="mr-1" />}
          Sync now
        </Button>
      </div>
    </div>
  );
}

function ItemsTab() {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async (nextOffset = 0, q = search) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sap/items`, {
        params: { search: q, limit: LIMIT, offset: nextOffset },
        headers: authHeaders(),
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(0, ""); }, []);   // initial

  const columns = detectColumns(rows);

  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px] flex items-center gap-2">
          <Search size={14} className="text-slate-400" />
          <Input
            placeholder="Search by material ID / product ID / description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(0, search)}
            data-testid="items-search-input"
            className="h-8"
          />
          <Button size="sm" onClick={() => load(0, search)} data-testid="items-search-btn">
            Search
          </Button>
        </div>
        <div className="text-xs text-slate-500">
          Showing <span className="num font-semibold text-slate-800">{rows.length}</span> of{" "}
          <span className="num font-semibold text-slate-800">{total}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="items-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => (
                <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length || 1} className="text-center py-8 text-slate-500">
                <Loader2 className="inline animate-spin mr-2" size={14} /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length || 1} className="text-center py-8 text-slate-500">
                No items cached yet. Trigger a sync above.
              </td></tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={r._id || i} className="border-t border-slate-100 hover:bg-slate-50">
                {columns.map((c) => (
                  <td key={c} className="px-3 py-2 mono text-xs align-top max-w-[320px] truncate"
                      title={String(r[c] ?? "")}>
                    {formatCell(r[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {total > LIMIT && (
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs">
          <div className="text-slate-500">
            Rows {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={offset === 0 || loading}
                    onClick={() => load(Math.max(0, offset - LIMIT), search)}
                    data-testid="items-prev-btn">
              Prev
            </Button>
            <Button size="sm" variant="outline"
                    disabled={offset + LIMIT >= total || loading}
                    onClick={() => load(offset + LIMIT, search)}
                    data-testid="items-next-btn">
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BomTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sap/bom`, {
        params: { limit: 100, offset: 0 },
        headers: authHeaders(),
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load BOMs");
    } finally {
      setLoading(false);
    }
  }, []);

  const openDetail = useCallback(async (row) => {
    // Show whatever we already have so the panel feels instant, then
    // enrich with the joined child material-inputs from the detail endpoint.
    setSelected(row);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sap/bom/${encodeURIComponent(row._id)}`,
        { headers: authHeaders() });
      setSelected(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load BOM detail");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = detectColumns(rows, "bom");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">BOM Headers</div>
          <div className="text-xs text-slate-500 num">{total} cached</div>
        </div>
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm" data-testid="bom-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={columns.length || 1} className="text-center py-8 text-slate-500">
                  <Loader2 className="inline animate-spin mr-2" size={14} /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={columns.length || 1} className="text-center py-8 text-slate-500">
                  No BOMs cached yet. Trigger a sync above.
                </td></tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={r._id || i}
                    onClick={() => openDetail(r)}
                    data-testid={`bom-row-${r._id || i}`}
                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selected?._id === r._id ? "bg-cyan-50" : ""}`}>
                  {columns.map((c) => (
                    <td key={c} className="px-3 py-2 mono text-xs align-top max-w-[220px] truncate"
                        title={String(r[c] ?? "")}>
                      {formatCell(r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">BOM Detail</div>
            <div className="text-xs text-slate-500">
              {selected
                ? <>Model: <span className="mono">{selected.ProductionModelID || selected._id}</span>
                    {selected.children_count != null && (
                      <> · <span className="font-semibold text-slate-700">{selected.children_count}</span> child components</>
                    )}
                  </>
                : "Click a row on the left to inspect."}
            </div>
          </div>
          {detailLoading && <Loader2 size={14} className="animate-spin text-slate-400" />}
        </div>
        <div className="p-4 max-h-[70vh] overflow-auto">
          {!selected && <div className="text-sm text-slate-500">Nothing selected.</div>}
          {selected && (
            <pre className="text-fiori-caption mono bg-slate-50 border border-slate-100 rounded-sm p-3 whitespace-pre-wrap break-all"
                 data-testid="bom-detail-json">
{JSON.stringify(selected, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function ProbeTab() {
  const [path, setPath] = useState("/sap/byd/odata/v1/");
  const [top, setTop] = useState(3);
  const [user, setUser] = useState("");   // blank = server-side fallback chain
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!path.startsWith("/")) {
      toast.error("Path must start with /"); return;
    }
    setLoading(true);
    setResult(null);
    try {
      const params = { path, top };
      if (user.trim()) params.sap_user = user.trim();
      const r = await axios.get(`${API}/admin/sap/probe`, {
        params,
        headers: authHeaders(),
      });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "/sap/byd/odata/v1/",
    "/sap/byd/odata/cust/v1/",
    "/sap/byd/odata/v1/khmaterialmaster/MaterialCollection",
    "/sap/byd/odata/v1/khbomcollection/BOMCollection",
    "/sap/byd/odata/v1/vmu/MaterialCollection",
    "/sap/byd/odata/v1/materialbytwo/MaterialCollection",
    "/sap/byd/odata/v1/billofmaterial/BOMCollection",
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
      <div className="text-sm text-slate-600">
        Explore any BYD OData path. Use this to discover the right service name
        for Item Master / BOM in this tenant. Never returns credentials.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_auto] gap-2">
        <div>
          <Label className="text-xs">Path</Label>
          <Input value={path} onChange={(e) => setPath(e.target.value)}
                 className="h-8 mono text-xs" data-testid="probe-path-input"
                 placeholder="/sap/byd/odata/..." />
        </div>
        <div>
          <Label className="text-xs">User (blank = auto)</Label>
          <Input value={user} onChange={(e) => setUser(e.target.value)}
                 className="h-8 mono text-xs" data-testid="probe-user-input"
                 placeholder="auto" />
        </div>
        <div>
          <Label className="text-xs">Top</Label>
          <Input type="number" min={1} max={20} value={top}
                 onChange={(e) => setTop(Number(e.target.value) || 3)}
                 className="h-8 num text-xs" data-testid="probe-top-input" />
        </div>
        <div className="flex items-end">
          <Button onClick={run} disabled={loading} data-testid="probe-run-btn" size="sm">
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />}
            Probe
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {suggestions.map((s) => (
          <button key={s} onClick={() => setPath(s)}
                  className="text-fiori-caption mono px-2 py-1 rounded-sm border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700"
                  data-testid={`probe-suggest-${s}`}>
            {s}
          </button>
        ))}
      </div>
      {result && (
        <pre className="text-fiori-caption mono bg-slate-50 border border-slate-100 rounded-sm p-3 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all"
             data-testid="probe-result">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

/* ------------- helpers ------------- */

function detectColumns(rows, kind = "items") {
  // Prefer a stable ordered subset of the most useful keys, then append
  // any extras the tenant returned so nothing is hidden.
  const preferred = kind === "bom"
    ? ["_id", "ProductionModelID", "MaterialUUID", "SiteUUID", "ID", "ObjectID"]
    : ["_id", "InternalID", "MaterialID", "ProductID", "BaseMeasureUnitCode",
       "Description", "MaterialDescription", "ProductCategoryID", "Category",
       "MaterialGroup", "Status", "RayItemcode_KUT"];
  const seen = new Set();
  for (const r of rows.slice(0, 10)) {
    for (const k of Object.keys(r)) {
      if (k.startsWith("_") && k !== "_id") continue;
      if (k === "_uri") continue;
      seen.add(k);
    }
  }
  const cols = [];
  for (const k of preferred) if (seen.has(k)) cols.push(k);
  for (const k of seen) if (!cols.includes(k)) cols.push(k);
  if (cols.length === 0) return ["_id"];
  return cols.slice(0, 6);   // cap width — was 8, tighter fits mobile better
}

function formatCell(v) {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// ---------------------------------------------------------------------------
// Data Cleanup tab — currently hosts the "purge zero-amount price-spec stubs"
// action (POST /list-prices/admin/purge-zero-stubs). Dry-run by default so an
// admin can eyeball the scope before pulling the trigger.
// ---------------------------------------------------------------------------
function CleanupTab() {
  const [supplier, setSupplier] = useState("");
  const [preview, setPreview] = useState(null); // dry-run response
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executed, setExecuted] = useState(null); // real-run response
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [rebuildArmed, setRebuildArmed] = useState(false);
  const [rebuildError, setRebuildError] = useState(null);
  // Purge raw props state
  const [propsArmed, setPropsArmed] = useState(false);
  const [propsBusy, setPropsBusy] = useState(false);
  const [propsResult, setPropsResult] = useState(null);
  const [propsError, setPropsError] = useState(null);

  async function runRebuild() {
    // Two-click confirm — first click arms the button (shows the red
    // "Confirm wipe & rebuild" prompt inline), second click executes.
    // Deliberately NOT using `window.confirm` — some embedded browsers
    // (webview containers, kiosk mode) silently no-op it, which was the
    // original "button does nothing" symptom.
    if (!rebuildArmed) {
      setRebuildArmed(true);
      setRebuildError(null);
      setTimeout(() => setRebuildArmed(false), 8000); // auto-disarm
      return;
    }
    setRebuildArmed(false);
    setRebuilding(true);
    setRebuildResult(null);
    setRebuildError(null);
    try {
      const r = await axios.post(
        `${API}/list-prices/admin/rebuild-active-mirror`,
        {},
        { headers: authHeaders(), timeout: 180000 });
      setRebuildResult(r.data);
      const drop = r.data.before_count - r.data.after_count;
      toast.success(
        `Mirror rebuilt · ${r.data.after_count} rows (was ${r.data.before_count}, `
        + `${drop > 0 ? `-${drop}` : `+${-drop}`}) · `
        + `${r.data.skipped_zero_amount} zero-amount rows filtered out at ingest`);
    } catch (e) {
      // Keep the error visible even if the toast gets dismissed —
      // rebuilds are important enough that "silent failure" is the
      // wrong default.
      const msg = e.response?.data?.detail
        || e.message
        || "Rebuild failed — see server logs";
      setRebuildError(`${e.response?.status ? `HTTP ${e.response.status} · ` : ""}${msg}`);
      toast.error(msg);
    } finally {
      setRebuilding(false);
    }
  }

  async function runPurgeProps() {
    if (!propsArmed) {
      setPropsArmed(true);
      setPropsError(null);
      setTimeout(() => setPropsArmed(false), 8000);
      return;
    }
    setPropsArmed(false);
    setPropsBusy(true);
    setPropsResult(null);
    setPropsError(null);
    try {
      const r = await axios.post(
        `${API}/list-prices/admin/purge-spec-props`,
        {},
        { headers: authHeaders(), timeout: 60000 });
      setPropsResult(r.data);
      const s = r.data.deleted?.sap_price_specs || 0;
      const p = r.data.deleted?.sap_price_spec_props || 0;
      toast.success(`Purged ${s} spec rows + ${p} prop rows · ${r.data.cache_clears} cache clears`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Purge failed";
      setPropsError(`${e.response?.status ? `HTTP ${e.response.status} · ` : ""}${msg}`);
      toast.error(msg);
    } finally {
      setPropsBusy(false);
    }
  }

  async function runDryRun() {
    setPreviewing(true);
    setExecuted(null);
    try {
      const params = new URLSearchParams({ dry_run: "true" });
      if (supplier.trim()) params.set("supplier_code", supplier.trim());
      const r = await axios.post(
        `${API}/list-prices/admin/purge-zero-stubs?${params}`,
        {},
        { headers: authHeaders() });
      setPreview(r.data);
      toast.success(`Preview: ${r.data.would_delete} row(s) match the cleanup filter`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed — admin token required");
    } finally {
      setPreviewing(false);
    }
  }

  async function runExecute() {
    if (!preview || preview.would_delete === 0) return;
    const scope = supplier.trim() ? `supplier ${supplier.trim()}` : "ALL suppliers";
    if (!window.confirm(
      `Delete ${preview.would_delete} zero-amount stub row(s) for ${scope}?\n\n`
      + `Impacts ${preview.affected_supplier_count} supplier(s) · `
      + `${preview.affected_material_count} material(s).\n\n`
      + `This cannot be undone. Real prices and manual drafts are protected by the filter.`
    )) return;
    setExecuting(true);
    try {
      const params = new URLSearchParams({ dry_run: "false" });
      if (supplier.trim()) params.set("supplier_code", supplier.trim());
      const r = await axios.post(
        `${API}/list-prices/admin/purge-zero-stubs?${params}`,
        {},
        { headers: authHeaders() });
      setExecuted(r.data);
      setPreview(null);
      toast.success(`Purged ${r.data.deleted} row(s). Caches cleared for ${r.data.affected_supplier_count} supplier(s).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setExecuting(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="cleanup-tab">
      {/* Card 0 — Wipe + rebuild active mirror (the "big hammer") */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-sm bg-cyan-50 border border-cyan-200 p-2 text-cyan-700">
            <RefreshCw size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">Rebuild active price mirror</div>
            <div className="text-xs text-slate-600 mt-1 max-w-2xl">
              Wipes <code className="mono bg-slate-100 px-1 rounded">sap_price_specs_active</code> and
              re-runs the analytics-report sync with the tightened filter
              (<code className="mono">KCAMOUNT_CONTENT gt 0M</code> + client-side zero-amount skip).
              Any phantom placeholder rows in the current mirror are gone after this — including ones
              the row-level purge below can't match. Runs synchronously
              (~10-30 s). Also clears the by-supplier and by-item response caches.
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Admin only. Endpoint: <code className="mono">POST /api/list-prices/admin/rebuild-active-mirror</code>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-slate-100">
          <Button
            onClick={runRebuild}
            disabled={rebuilding}
            size="sm"
            className={`h-8 text-white ${
              rebuildArmed
                ? "bg-rose-600 hover:bg-rose-700 ring-2 ring-rose-300 animate-pulse"
                : "bg-cyan-700 hover:bg-cyan-800"
            }`}
            data-testid="rebuild-mirror-btn"
          >
            {rebuilding
              ? <><Loader2 size={13} className="mr-1 animate-spin" />
                  Rebuilding — SAP fetch in progress…</>
              : rebuildArmed
              ? <><AlertTriangle size={13} className="mr-1" />
                  Click again to confirm wipe & rebuild</>
              : <><RefreshCw size={13} className="mr-1" />
                  Wipe & rebuild from SAP</>}
          </Button>
          {rebuildArmed && !rebuilding && (
            <div className="text-xs text-rose-700 font-medium">
              This wipes <code className="mono bg-rose-50 px-1 rounded">sap_price_specs_active</code> —
              click the red button within 8 s to proceed.
            </div>
          )}
          {rebuildResult && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-0.5"
                 data-testid="rebuild-mirror-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={13} />
                <span className="font-semibold">Rebuilt successfully</span>
              </div>
              <div>
                Before: <span className="font-semibold">{rebuildResult.before_count}</span> rows ·
                {" "}After: <span className="font-semibold">{rebuildResult.after_count}</span> rows ·
                {" "}Δ: <span className="font-semibold">{rebuildResult.delta >= 0 ? "+" : ""}{rebuildResult.delta}</span>
              </div>
              <div>
                SAP returned <span className="font-semibold">{rebuildResult.raw_rows_from_sap}</span> raw rows ·
                {" "}<span className="font-semibold">{rebuildResult.skipped_zero_amount}</span> zero-amount filtered ·
                {" "}<span className="font-semibold">{rebuildResult.cache_clears}</span> cache entr(y|ies) cleared ·
                {" "}<span className="font-semibold">{rebuildResult.elapsed_ms} ms</span>
              </div>
            </div>
          )}
          {rebuildError && (
            <div className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 flex items-center gap-2"
                 data-testid="rebuild-mirror-error">
              <AlertTriangle size={13} />
              <span className="font-semibold">Rebuild failed:</span>
              <span className="mono">{rebuildError}</span>
            </div>
          )}
        </div>
      </div>

      {/* Card 1 — Purge raw price-spec props (dead-code fallback source) */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-sm bg-amber-50 border border-amber-200 p-2 text-amber-700">
            <Trash2 size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">Wipe raw price-spec props (dead-code fallback source)</div>
            <div className="text-xs text-slate-600 mt-1 max-w-2xl">
              Drops <code className="mono bg-slate-100 px-1 rounded">sap_price_specs</code> and
              {" "}<code className="mono bg-slate-100 px-1 rounded">sap_price_spec_props</code> —
              the two legacy full-history mirrors that used to feed the by-supplier / by-item
              fallback path. This is the collection that caused N1628 to show
              3,839 phantom rows: unfiltered placeholder specs from an early sync.
              The endpoints no longer fall back per-supplier, so these tables are dead code
              paths. Wiping them removes the loaded gun so a future refactor can't
              re-introduce the bug.
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Admin only. Endpoint: <code className="mono">POST /api/list-prices/admin/purge-spec-props</code>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-slate-100">
          <Button
            onClick={runPurgeProps}
            disabled={propsBusy}
            size="sm"
            className={`h-8 text-white ${
              propsArmed
                ? "bg-rose-600 hover:bg-rose-700 ring-2 ring-rose-300 animate-pulse"
                : "bg-amber-600 hover:bg-amber-700"
            }`}
            data-testid="purge-props-btn"
          >
            {propsBusy
              ? <><Loader2 size={13} className="mr-1 animate-spin" /> Purging…</>
              : propsArmed
              ? <><AlertTriangle size={13} className="mr-1" /> Click again to confirm wipe</>
              : <><Trash2 size={13} className="mr-1" /> Wipe raw price-spec props</>}
          </Button>
          {propsArmed && !propsBusy && (
            <div className="text-xs text-rose-700 font-medium">
              Deletes ALL rows from both raw-props collections — click within 8 s to proceed.
            </div>
          )}
          {propsResult && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
                 data-testid="purge-props-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={13} />
                <span className="font-semibold">Wiped successfully</span>
              </div>
              <div className="mt-0.5">
                <code className="mono">sap_price_specs</code>: {propsResult.before?.sap_price_specs} → 0
                {" · "}
                <code className="mono">sap_price_spec_props</code>: {propsResult.before?.sap_price_spec_props} → 0
                {" · "}
                <span className="font-semibold">{propsResult.cache_clears}</span> cache entr(y|ies) cleared
              </div>
            </div>
          )}
          {propsError && (
            <div className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 flex items-center gap-2"
                 data-testid="purge-props-error">
              <AlertTriangle size={13} />
              <span className="font-semibold">Purge failed:</span>
              <span className="mono">{propsError}</span>
            </div>
          )}
        </div>
      </div>

      {/* Card 2 — Purge zero-amount stubs (surgical row-level) */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-sm bg-rose-50 border border-rose-200 p-2 text-rose-700">
            <ShieldAlert size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">Purge zero-amount price-spec stubs</div>
            <div className="text-xs text-slate-600 mt-1 max-w-2xl">
              Removes rows from <code className="mono bg-slate-100 px-1 rounded">sap_price_specs_active</code> where
              amount is 0/null <strong>AND</strong> no SAP UUID is present <strong>AND</strong> the row wasn't manually created.
              These are phantom stubs left over from an early full-sync that pulled every placeholder price spec.
              Real prices and buyer-created drafts are safe.
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Admin only. Endpoint: <code className="mono">POST /api/list-prices/admin/purge-zero-stubs</code>
            </div>
          </div>
        </div>

        {/* Scope + action controls */}
        <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-slate-100">
          <div className="flex flex-col gap-1">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Supplier code (optional)
            </Label>
            <Input
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="e.g. N1628 (blank = all)"
              className="mono h-8 text-sm w-[220px]"
              data-testid="cleanup-supplier-input"
            />
          </div>
          <Button
            onClick={runDryRun}
            disabled={previewing || executing}
            size="sm"
            variant="outline"
            className="h-8"
            data-testid="cleanup-preview-btn"
          >
            {previewing ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Eye size={13} className="mr-1" />}
            Preview (dry-run)
          </Button>
          <Button
            onClick={runExecute}
            disabled={!preview || preview.would_delete === 0 || executing}
            size="sm"
            className="h-8 bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="cleanup-execute-btn"
          >
            {executing ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Trash2 size={13} className="mr-1" />}
            Execute cleanup
          </Button>
        </div>

        {/* Preview panel */}
        {preview && (
          <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2"
               data-testid="cleanup-preview-result">
            <div className="flex flex-wrap items-center gap-4">
              <div><span className="text-amber-700">Would delete:</span> <span className="font-bold text-amber-900">{preview.would_delete}</span> row(s)</div>
              <div><span className="text-amber-700">Affected suppliers:</span> <span className="font-semibold">{preview.affected_supplier_count}</span></div>
              <div><span className="text-amber-700">Affected materials:</span> <span className="font-semibold">{preview.affected_material_count}</span></div>
              <div><span className="text-amber-700">Scope:</span> <span className="mono">{preview.supplier_scope}</span></div>
            </div>
            {preview.sample?.length > 0 && (
              <div>
                <div className="text-amber-700 mt-1 mb-0.5">Sample (first {preview.sample.length}):</div>
                <div className="rounded-sm bg-white border border-amber-200 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-50 text-amber-800">
                      <tr>
                        <th className="text-left px-2 py-1">supplier</th>
                        <th className="text-left px-2 py-1">material</th>
                        <th className="text-right px-2 py-1">amount</th>
                        <th className="text-left px-2 py-1">uom</th>
                        <th className="text-left px-2 py-1">source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.map((s, i) => (
                        <tr key={s._id || i} className="border-t border-amber-100">
                          <td className="px-2 py-1 mono">{s.supplier_code || "—"}</td>
                          <td className="px-2 py-1 mono">{s.material_code || "—"}</td>
                          <td className="px-2 py-1 text-right num">{s.amount ?? "null"}</td>
                          <td className="px-2 py-1">{s.unit_code || "—"}</td>
                          <td className="px-2 py-1 text-slate-600">{s._source || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {preview.would_delete === 0 && (
              <div className="italic text-amber-700">Nothing to clean up for this scope — mirror is already clean.</div>
            )}
          </div>
        )}

        {/* Execute result */}
        {executed && (
          <div className="mt-2 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900"
               data-testid="cleanup-execute-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} />
              <div>
                Deleted <span className="font-bold">{executed.deleted}</span> row(s) ·
                cleared <span className="font-semibold">{executed.cache_clears}</span> cache entr(y|ies) ·
                impacted <span className="font-semibold">{executed.affected_supplier_count}</span> supplier(s) ·
                <span className="font-semibold"> {executed.affected_material_count}</span> material(s).
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

